/**
 * CouponSelectionScreenView - Presentation Component
 *
 * Full-screen coupon selection with input, valid/invalid sections, and action button.
 *
 * @see mobile: module-flash-sale/sales-rule/screens/coupon-screeen/
 */

'use client';

import React, { useCallback, useMemo, useState } from 'react';
import { cn, Text, Button, ActivityIndicator } from '@kidslabs/mario-nextjs-react-ui-core';
import { CouponCard } from '../coupon-card';
import { CouponDetailModal } from '../coupon-detail-modal';
import type { SalesRule } from '@kidslabs/module-flash-sale';
import styles from './coupon-selection-screen.module.css';

interface CouponSectionType {
    isDisabled?: boolean;
    title: string;
    data: SalesRule[];
}

interface CouponSelectionScreenViewProps {
    variant?: 'mini' | 'full';
    couponSections: CouponSectionType[];
    selectedCoupon?: SalesRule;
    appliedCouponCode?: string | null;
    isLoading: boolean;
    isProcessing: boolean;
    couponText: string;
    errorMessage?: string;
    isButtonDisabled?: boolean;
    isSelectedCoupon?: string | boolean;
    onSelectCoupon: (coupon: SalesRule) => void;
    onChangeText: (text: string) => void;
    onApplyCoupon: (code: string) => void;
    onSubmit: () => void;
    onRefresh?: () => void;
    onClose: () => void;
}

export const CouponSelectionScreenView: React.FC<CouponSelectionScreenViewProps> = React.memo(
    ({
        variant = 'mini',
        couponSections,
        selectedCoupon,
        isLoading,
        isProcessing,
        couponText,
        errorMessage,
        isButtonDisabled,
        isSelectedCoupon,
        onSelectCoupon,
        onChangeText,
        onApplyCoupon,
        onSubmit,
    }) => {
        const [detailCoupon, setDetailCoupon] = useState<SalesRule | null>(null);
        const isInputDisabled = useMemo(() => !couponText?.trim() || isProcessing, [couponText, isProcessing]);

        const handleInputSubmit = useCallback(() => {
            if (!isInputDisabled) {
                onApplyCoupon(couponText);
            }
        }, [isInputDisabled, onApplyCoupon, couponText]);

        const handleKeyDown = useCallback(
            (event: React.KeyboardEvent) => {
                if (event.key === 'Enter') {
                    handleInputSubmit();
                }
            },
            [handleInputSubmit]
        );

        const handleShowDetail = useCallback((coupon: SalesRule) => {
            setDetailCoupon(coupon);
        }, []);

        const handleCloseDetail = useCallback(() => {
            setDetailCoupon(null);
        }, []);

        const submitButtonLabel = useMemo(() => {
            if (isProcessing) return 'Đang xử lý...';
            if (isSelectedCoupon) return 'Bỏ sử dụng';
            return 'Sử dụng';
        }, [isProcessing, isSelectedCoupon]);

        if (isLoading) {
            return (
                <div className={cn(styles.container, variant === 'full' && styles.containerFull)}>
                    <div className={styles.loadingContainer}>
                        <ActivityIndicator size="large" />
                    </div>
                </div>
            );
        }

        return (
            <div className={cn(styles.container, variant === 'full' && styles.containerFull)}>
                {/* Manual code input */}
                <div className={styles.inputSection}>
                    <div className={styles.inputWrapper}>
                        <input
                            type="text"
                            className={cn(styles.input, errorMessage && styles.inputError)}
                            placeholder="Nhập mã khuyến mãi"
                            value={couponText}
                            onChange={e => onChangeText(e.target.value)}
                            onKeyDown={handleKeyDown}
                            aria-label="Nhập mã khuyến mãi"
                        />
                        <Button
                            variant="contained"
                            colorRole="primary"
                            size="small"
                            disabled={isInputDisabled}
                            onPress={handleInputSubmit}
                            className={styles.inputButton}
                        >
                            <Text size="small" weight="medium" className={styles.inputButtonText}>
                                {isProcessing ? 'Đang xử lý...' : 'Thêm mã'}
                            </Text>
                        </Button>
                    </div>
                    {errorMessage && (
                        <Text size="small" className={styles.errorText}>
                            {errorMessage}
                        </Text>
                    )}
                </div>

                {/* Coupon sections */}
                <div className={styles.sectionsContainer}>
                    {couponSections.map(section => {
                        if (!section.data.length) return null;
                        return (
                            <div key={section.title} className={styles.section}>
                                <Text size="medium" weight="bold" className={styles.sectionTitle}>
                                    {section.title}
                                </Text>
                                <div className={styles.sectionList} role="radiogroup" aria-label={section.title}>
                                    {section.data.map(coupon => {
                                        const couponAsContent = {
                                            ...coupon,
                                            salesrule: {
                                                salesrule_id: coupon.code ?? '',
                                                code: coupon.code,
                                                name: coupon.name,
                                                description: coupon.description,
                                                from_date: coupon.from_date,
                                                to_date: coupon.to_date,
                                            },
                                        };

                                        // Extract ineligibility data from UnifiedSalesRule
                                        const unifiedCoupon = coupon as SalesRule & {
                                            ineligibilityReason?: string;
                                            applicableAmount?: number;
                                        };
                                        const ineligibilityReasons = unifiedCoupon.ineligibilityReason
                                            ? [unifiedCoupon.ineligibilityReason]
                                            : undefined;

                                        return (
                                            <CouponCard
                                                key={coupon.code}
                                                coupon={couponAsContent as never}
                                                isSelectable={!section.isDisabled}
                                                isSelected={selectedCoupon?.code === coupon.code}
                                                isDisabled={section.isDisabled}
                                                ineligibilityReasons={ineligibilityReasons}
                                                applicableAmount={unifiedCoupon.applicableAmount}
                                                onSelect={() => onSelectCoupon(coupon)}
                                                onShowCondition={() => handleShowDetail(coupon)}
                                            />
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Footer action button */}
                <div className={styles.footer}>
                    <Button
                        variant="contained"
                        colorRole={isSelectedCoupon ? 'secondary' : 'primary'}
                        size="large"
                        disabled={isButtonDisabled}
                        onPress={onSubmit}
                        className={styles.submitButton}
                    >
                        <Text size="medium" weight="bold" className={styles.submitButtonText}>
                            {submitButtonLabel}
                        </Text>
                    </Button>
                </div>

                {/* Detail modal */}
                {detailCoupon && (
                    <CouponDetailModal
                        coupon={detailCoupon as never}
                        showAddButton={false}
                        onClose={handleCloseDetail}
                    />
                )}
            </div>
        );
    }
);

CouponSelectionScreenView.displayName = 'CouponSelectionScreenView';
